import {
  API_PREFIX,
  HttpError,
  paginatedFromDto,
  type HttpClient,
} from "@nasam/kernel";
import type { SensorAssignmentsRepository } from "../domain";
import {
  sensorAssignmentsListResponseDtoSchema,
  sensorCreateInputDtoSchema,
  sensorCreateResponseDtoSchema,
  sensorDeleteResponseDtoSchema,
  sensorDetailResponseDtoSchema,
  sensorDeviceSensorsResponseDtoSchema,
  sensorFormResponseDtoSchema,
  sensorIoConflictResponseDtoSchema,
  sensorUpdateInputDtoSchemaFor,
  sensorUpdateResponseDtoSchema,
} from "./sensorAssignmentDtos";
import {
  sensorAssignmentListItemFromDto,
  sensorCreateInputToDto,
  sensorCreateResultDataFromDto,
  sensorDetailFromDto,
  sensorFormMetaFromDto,
  sensorIoConflictEntryFromDto,
  sensorUpdateInputToDto,
} from "./sensorAssignmentMappers";

const SENSORS_PATH = `${API_PREFIX}/sensors`;
const DEVICES_PATH = `${API_PREFIX}/devices`;

export function createSensorAssignmentsHttpRepository(
  http: HttpClient,
): SensorAssignmentsRepository {
  return {
    async list(query) {
      const response = await http.get(SENSORS_PATH, { params: query });
      return paginatedFromDto(
        sensorAssignmentsListResponseDtoSchema.parse(response),
        sensorAssignmentListItemFromDto,
      );
    },
    async getForm(sensorType) {
      const response = await http.get(`${SENSORS_PATH}/form`, {
        params: { sensorType },
      });
      return sensorFormMetaFromDto(
        sensorFormResponseDtoSchema.parse(response).data,
      );
    },
    async create(input) {
      try {
        const response = await http.post(
          SENSORS_PATH,
          sensorCreateInputDtoSchema.parse(sensorCreateInputToDto(input)),
        );
        const parsed = sensorCreateResponseDtoSchema.parse(response);
        return {
          status: "created",
          ...sensorCreateResultDataFromDto(parsed.data),
        };
      } catch (error) {
        // All-or-nothing write -- one device with a clashing IO fails the
        // whole call. Disambiguated by `code`, never `message` (translated).
        const conflict =
          error instanceof HttpError && error.status === 409
            ? sensorIoConflictResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (conflict === undefined || !conflict.success) throw error;
        return {
          status: "io-conflict",
          message: conflict.data.message,
          conflicts: conflict.data.data.map(sensorIoConflictEntryFromDto),
        };
      }
    },
    async getById(sensorId) {
      const response = await http.get(`${SENSORS_PATH}/${sensorId}`);
      return sensorDetailFromDto(
        sensorDetailResponseDtoSchema.parse(response).data,
      );
    },
    async update(sensorId, input) {
      try {
        const response = await http.put(
          `${SENSORS_PATH}/${sensorId}`,
          sensorUpdateInputDtoSchemaFor(input.sensorType).parse(
            sensorUpdateInputToDto(input),
          ),
        );
        const parsed = sensorUpdateResponseDtoSchema.parse(response);
        return { status: "updated", sensor: sensorDetailFromDto(parsed.data) };
      } catch (error) {
        const conflict =
          error instanceof HttpError && error.status === 409
            ? sensorIoConflictResponseDtoSchema.safeParse(error.data)
            : undefined;
        if (conflict === undefined || !conflict.success) throw error;
        return {
          status: "io-conflict",
          message: conflict.data.message,
          conflicts: conflict.data.data.map(sensorIoConflictEntryFromDto),
        };
      }
    },
    async delete(sensorId) {
      const response = await http.delete(`${SENSORS_PATH}/${sensorId}`);
      const { message } = sensorDeleteResponseDtoSchema.parse(response);
      return { responseMessage: message };
    },
    async getDeviceSensors(deviceId) {
      const response = await http.get(`${DEVICES_PATH}/${deviceId}/sensors`);
      return sensorDeviceSensorsResponseDtoSchema
        .parse(response)
        .data.map(sensorDetailFromDto);
    },
  };
}
